import React, { useState, useRef, useEffect } from "react";
import {
  Flame,
  BookOpen,
  Target,
  Brain,
  // TrendingUp,
  // TrendingDown,
  ChevronLeft,
  ChevronRight,
  Languages,
  RotateCw,
} from "lucide-react";
import assets from "../../assets";
import "./style.sass";
import {
  ROUTE_CHAT,
  ROUTE_LEARNING_ASSETS,
  ROUTE_QUIZ,
  ROUTE_MOCK_TEST,
} from "../../route";
import { useNavigate, useLocation } from "react-router-dom";
import { useCommonDashboard } from "../../hooks/common/dashboard";
import { MediaPopup } from "../../common-components/media-popup";
import { useApi } from "../../hooks/use-api";
import { useSingleton } from "../../hooks/use-singleton";
import { API_VIDEO_MARK, API_VIDEO_CHAT, apiStudentUpcomingTests } from "../../utils/api-routes";
import useHandler from "./handler";
import { COOKIE_ACCESS_SUBSCRIBED_COURSES } from "../../utils/storage-constants";
import { hasCourseAccess } from "../../hooks/common/courses";
import { useNotify } from "../../hooks/use-notify";

/* ── Dummy Data ── */
const dummyDashboardData = {
  recent_videos: [
    {
      id: 1,
      title: "Introduction to Quantum Entanglement",
      video_url: "https://www.w3schools.com/html/mov_bbb.mp4",
      duration: "12:45",
      subtitle: "Module 4 · Prof. Sarah Jenkins",
    },
    {
      id: 2,
      title: "Advanced Logic Design & Patterns",
      video_url: "https://www.w3schools.com/html/mov_bbb.mp4",
      duration: "08:20",
      subtitle: "Module 2 · Tech Lead Mike Chen",
    },
  ],
};

/* ── Action Cards Config ── */
const defaultActionCards = [
  {
    title: "Rovian AI Chat",
    desc: "Get instant help from AI tutor with personalized feedback and 24/7 availability.",
    btnText: "Start Asking",
    color: "blue1",
    icon: assets.ic_active_dashboard_moniter,
    route: ROUTE_CHAT,
  },
  {
    title: "Learning Videos",
    desc: "Watch curated educational content from top instructors around the globe.",
    btnText: "Watch & Learn",
    color: "green1",
    icon: assets.ic_active_dashboard_video,
    route: ROUTE_LEARNING_ASSETS,
  },
  {
    title: "Take Quiz",
    desc: "Test your knowledge with MCQ quizzes designed to track your progress.",
    btnText: "Start Quiz",
    color: "purple1",
    icon: assets.ic_active_dashboard_burger,
    route: ROUTE_QUIZ,
  },
  {
    title: "Practice Test",
    desc: "Test your knowledge with MCQ quizzes in a timed, exam-like environment.",
    btnText: "Start Test",
    color: "orange1",
    icon: assets.ic_active_dashboard_descriptive,
    route: ROUTE_MOCK_TEST,
    isDynamic: true,
  },
];

/* ── Helpers ── */
const getRecommendedVideos = (dashboardData) => {
  const videoProgress = dashboardData?.video_progress?.videos;
  if (!Array.isArray(videoProgress) || videoProgress.length === 0) return [];

  const seen = new Set();
  const recommended = [];

  videoProgress.forEach((vp) => {
    if (Array.isArray(vp.recommended_videos)) {
      vp.recommended_videos.forEach((rv) => {
        if (!seen.has(rv.video_url)) {
          seen.add(rv.video_url);
          recommended.push({
            id: rv.video_url,
            title: rv.display_name || rv.chapter,
            video_url: rv.video_url,
            duration: "",
            subtitle: rv.chapter,
            filters: {
              ...(vp.exam && { exam: vp.exam }),
              ...(vp.subject && { subject: vp.subject }),
              ...(rv.chapter && { chapter: rv.chapter }), // Chapter specific to recommended video
              ...(vp.board && { board: vp.board }),
              ...(vp.clas && { clas: vp.clas }),
              ...(vp.medium && { medium: vp.medium }),
              ...(vp.part && { part: vp.part }),
              ...(vp.unit && { unit: vp.unit }),
              ...(vp.lesson && { lesson: vp.lesson }),
            },
          });
        }
      });
    }
  });

  return recommended;
};

const getRecentVideos = (videos, dashboardData) => {
  let list = [];
  if (Array.isArray(videos) && videos.length > 0) {
    list = videos;
  } else if (
    Array.isArray(dashboardData?.recent_videos) &&
    dashboardData.recent_videos.length > 0
  ) {
    list = dashboardData.recent_videos;
  } else {
    list = dummyDashboardData.recent_videos;
  }

  return list.map((item) => ({
    id: item.asset_id || item.id,
    title: item.display_name || item.title,
    video_url: item.url || item.video_url,
    duration: item.duration || "",
    subtitle: item.subtitle || item.chapter || "",
    filters: {
      ...(item.exam && { exam: item.exam }),
      ...(item.subject && { subject: item.subject }),
      ...(item.chapter && { chapter: item.chapter }),
      ...(item.board && { board: item.board }),
      ...(item.clas && { clas: item.clas }),
      ...(item.medium && { medium: item.medium }),
      ...(item.part && { part: item.part }),
      ...(item.unit && { unit: item.unit }),
      ...(item.lesson && { lesson: item.lesson }),
      ...(item.filters && { ...item.filters }), // Merge existing filters if present
    },
  }));
};

/* ── Carousel Modal ── */
const VideoCarouselModal = ({ videos, onClose, onPlay }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(videos.length / itemsPerPage);

  const prev = () => setCurrentIndex((i) => Math.max(i - 1, 0));
  const next = () => setCurrentIndex((i) => Math.min(i + 1, totalPages - 1));

  const visibleVideos = videos.slice(
    currentIndex * itemsPerPage,
    currentIndex * itemsPerPage + itemsPerPage,
  );

  return (
    <div className="video-modal">
      <div className="video-modal-content large">
        <button className="close-btn" onClick={onClose}>
          ✖
        </button>
        <div className="carousel-header">
          <div className="carousel-title-row">
            <div className="carousel-icon-box">🎬</div>
            <div>
              <h3 className="carousel-title">Video</h3>
              <p className="carousel-subtitle">
                {videos.length} videos · Generated by Rovian Learning AI
              </p>
            </div>
          </div>
        </div>

        <div className="carousel-wrapper">
          <button
            className="carousel-nav prev"
            onClick={prev}
            disabled={currentIndex === 0}
          >
            <ChevronLeft size={22} />
          </button>

          <div className="carousel-grid-3">
            {visibleVideos.map((video) => (
              <div key={video.id} className="carousel-video-card">
                <div className="carousel-thumb-wrap">
                  <video
                    src={video.video_url}
                    muted
                    preload="metadata"
                    className="carousel-thumb"
                  />
                </div>
                <button
                  className="carousel-play-btn"
                  onClick={() => {
                    onPlay(video);
                    onClose();
                  }}
                >
                  ▶ Play
                </button>
                <p className="carousel-chapter-label">
                  {video.subtitle || video.title}
                </p>
              </div>
            ))}
          </div>

          <button
            className="carousel-nav next"
            onClick={next}
            disabled={currentIndex >= totalPages - 1}
          >
            <ChevronRight size={22} />
          </button>
        </div>

        <div className="carousel-dots">
          {Array.from({ length: totalPages }).map((_, i) => (
            <span
              key={i}
              className={`dot ${i === currentIndex ? "active" : ""}`}
              onClick={() => setCurrentIndex(i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Action Card ── */
const ActionCard = ({
  title,
  desc,
  btnText,
  color,
  icon,
  route,
  isDynamic,
  examType,
  role,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleNotify } = useNotify();

  const subscribedCourses = JSON.parse(
    localStorage.getItem(COOKIE_ACCESS_SUBSCRIBED_COURSES) || "[]",
  );

  const hasCoures = hasCourseAccess(subscribedCourses, examType);

  const handleClick = () => {
    if (!route) return;
    if (!hasCoures && role !== "guest") {
      toggleNotify({
        type: "warning",
        title: "Subscription Required",
        description: "Please subscribe to access this feature.",
      });
      return;
    }
    const basePath = location.pathname.split("/dashboard")[0];
    if (isDynamic) {
      navigate(basePath === "" ? "/descriptive" : `${basePath}/mock-test`);
      return;
    }
    navigate(basePath === "" ? route : `${basePath}${route}`);
  };

  return (
    <div className={`action-card ${color}`} onClick={handleClick}>
      <div className="card-content">
        <div className="icon-wrap">
          <img src={icon} alt={title} />
        </div>
        <h4>{title}</h4>
        <p>{desc}</p>
        <button
          className="action-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
        >
          {btnText}
        </button>
      </div>
      <div className="blob-decoration"></div>
    </div>
  );
};

/* ── Goal Modal ── */
const goals = [
  { id: 1, label: "7 Days" },
  { id: 2, label: "15 Days" },
  { id: 3, label: "30 Days" },
  { id: 4, label: "60 Days" },
  { id: 5, label: "Custom" },
];

function GoalModal({ createChallenge, setShowGoalModal }) {
  const [selected, setSelected] = useState(5);
  const [days, setDays] = useState("");

  return (
    <section className="goal_modal_overlay">
      <section className="goal_modal">
        <button className="goal_close">✕</button>
        <section className="goal_icon">🔥</section>
        <h2>Set Your Learning Streak</h2>
        <p>Stay consistent by choosing how many days you'd like to practice.</p>
        <section className="goal_grid">
          {goals.map((goal) => (
            <section
              key={goal.id}
              className={`goal_card ${selected === goal.id ? "active" : ""}`}
              onClick={() => {
                setSelected(goal.id);
                setDays(goal.id !== 5 ? goal.label : "");
              }}
            >
              <input type="radio" checked={selected === goal.id} readOnly />
              <span>{goal?.label}</span>
              <section className="goal_calendar">
                <img
                  src={
                    goal.id === 1
                      ? assets.ic_calender_violet
                      : goal.id === 2
                        ? assets.ic_calender_green
                        : goal.id === 4
                          ? assets.ic_calender_yellow
                          : assets.ic_calender_orange
                  }
                  alt="calendar"
                />
              </section>
            </section>
          ))}
        </section>
        {selected === 5 && (
          <section className="goal_input_group">
            <label>Goal Duration (Days)</label>
            <input
              placeholder="Enter number of days"
              value={days}
              onChange={(e) => setDays(e.target.value)}
            />
          </section>
        )}
        <section className="goal_footer">
          <button className="goal_skip" onClick={setShowGoalModal}>
            Skip for now
          </button>
          <button className="goal_create" onClick={() => createChallenge(days)}>
            Create Goal
          </button>
        </section>
      </section>
    </section>
  );
}

/* ── Stat Card ── */
const StatCard = ({ icon, title, value, total, progress, color }) => (
  <section className="stat_card">
    <section className={`stat_icon ${color}`}>
      <img src={icon} alt={title} />
    </section>
    <section className="stat_content">
      <h4>{title}</h4>
      {total !== undefined ? (
        <>
          <p>
            {value} / {total} Completed
          </p>
          <section className="progress">
            <section className="fill" style={{ width: `${progress}%` }} />
          </section>
          <span>{Math.round(progress)}%</span>
        </>
      ) : (
        <h3>{value}</h3>
      )}
    </section>
  </section>
);

/* ── Goal Success Modal ── */
function GoalSuccessModal({ learningRes, onClose }) {
  const videoProgress = learningRes?.target_videos
    ? (learningRes?.videos_completed / learningRes?.target_videos) * 100
    : 0;
  const quizProgress = learningRes?.target_quiz_questions
    ? (learningRes?.mcqs_completed / learningRes?.target_quiz_questions) * 100
    : 0;
  const overallProgress =
    learningRes?.target_videos + learningRes?.target_quiz_questions
      ? ((learningRes?.videos_completed + learningRes?.mcqs_completed) /
          (learningRes?.target_videos + learningRes?.target_quiz_questions)) *
        100
      : 0;

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  return (
    <section className="goal_overlay">
      <section className="goal_success_modal">
        <button className="close_btn" onClick={onClose}>
          ✕
        </button>
        <section className="success_circle">
          <img src={assets.ic_success_tick_round} alt="success" />
        </section>
        <h2 className="goal_success_heading">Your Streak Goal is Active! 🎉</h2>
        <p className="goal_sub_text">
          Great choice! Stay consistent and achieve your goal.
        </p>
        <section className="goal_status_wrapper">
          <section className="goal_status">
            <span>GOAL STATUS</span>
            <label>{learningRes?.goal_status}</label>
          </section>
        </section>
        <h4 className="goal_duration_text">Goal Duration</h4>
        <section className="duration_card">
          <section className="left">
            <img src={assets.ic_calendar} alt="calendar" />
            <h3>{learningRes?.goal_period_days} Days</h3>
          </section>
          <section className="date">
            <span>START DATE</span>
            <strong>{formatDate(learningRes?.goal_start_date)}</strong>
          </section>
          <section className="date">
            <span>END DATE</span>
            <strong>{formatDate(learningRes?.goal_end_date)}</strong>
          </section>
        </section>
        <section className="stats_grid">
          <StatCard
            icon={assets.ic_video_blue_circle}
            color="purple"
            title="Videos"
            value={learningRes?.videos_completed}
            total={learningRes?.target_videos}
            progress={videoProgress}
          />
          <StatCard
            icon={assets.ic_book_blue}
            color="lightblue"
            title="Quiz Questions"
            value={learningRes?.mcqs_completed}
            total={learningRes?.target_quiz_questions}
            progress={quizProgress}
          />
          <StatCard
            icon={assets.ic_note_green}
            color="green"
            title="Previous Year Questions"
            value={learningRes?.pyq_completed ? "Completed" : "Not Started"}
          />
          <StatCard
            icon={assets.ic_fire_orange}
            color="lightorange"
            title="Daily Streak"
            value={`${learningRes?.daily_streak} Days`}
          />
          <StatCard
            icon={assets.ic_price_dark_red}
            color="red"
            title="Current Goal Streak"
            value={`${learningRes?.current_goal_streak} Days`}
          />
          <StatCard
            icon={assets.ic_graph_blue}
            color="indigo"
            title="Overall Progress"
            value={`${Math.round(overallProgress)}%`}
          />
        </section>
        <section className="goal_warning_box">
          💡 Complete at least one learning activity every day to keep your
          streak alive.
          <br />
          Missing a day resets your daily streak, but your completed videos,
          quizzes and overall progress remain saved.
        </section>
        <button className="continue_btn" onClick={onClose}>
          Continue Learning →
        </button>
      </section>
    </section>
  );
}

/* ── Goal Success View Challange Modal ── */
function GoalSuccessViewChanllengeModal({ learningRes, onClose }) {
  const videoProgress = learningRes?.target_videos
    ? (learningRes?.videos_completed / learningRes?.target_videos) * 100
    : 0;
  const quizProgress = learningRes?.target_quiz_questions
    ? (learningRes?.mcqs_completed / learningRes?.target_quiz_questions) * 100
    : 0;
  const overallProgress =
    learningRes?.target_videos + learningRes?.target_quiz_questions
      ? ((learningRes?.videos_completed + learningRes?.mcqs_completed) /
          (learningRes?.target_videos + learningRes?.target_quiz_questions)) *
        100
      : 0;

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  return (
    <section className="goal_overlay">
      <section className="goal_success_modal">
        <button className="close_btn" onClick={onClose}>
          ✕
        </button>
        <section className="success_circle">
          <img src={assets.ic_success_tick_round} alt="success" />
        </section>
        <h2 className="goal_success_heading">Your Streak Goal is Active! 🎉</h2>
        <p className="goal_sub_text">
          Great choice! Stay consistent and achieve your goal.
        </p>
        <section className="goal_status_wrapper">
          <section className="goal_status">
            <span>GOAL STATUS</span>
            <label>{learningRes?.goal_status}</label>
          </section>
        </section>
        <h4 className="goal_duration_text">Goal Duration</h4>
        <section className="duration_card">
          <section className="left">
            <img src={assets.ic_calendar} alt="calendar" />
            <h3>{learningRes?.goal_period_days} Days</h3>
          </section>
          <section className="date">
            <span>START DATE</span>
            <strong>{formatDate(learningRes?.goal_start_date)}</strong>
          </section>
          <section className="date">
            <span>END DATE</span>
            <strong>{formatDate(learningRes?.goal_end_date)}</strong>
          </section>
        </section>
        <section className="stats_grid">
          <StatCard
            icon={assets.ic_video_blue_circle}
            color="purple"
            title="Videos"
            value={learningRes?.videos_completed}
            total={learningRes?.target_videos}
            progress={videoProgress}
          />
          <StatCard
            icon={assets.ic_book_blue}
            color="lightblue"
            title="Quiz Questions"
            value={learningRes?.mcqs_completed}
            total={learningRes?.target_quiz_questions}
            progress={quizProgress}
          />
          <StatCard
            icon={assets.ic_note_green}
            color="green"
            title="Previous Year Questions"
            value={learningRes?.pyq_completed ? "Completed" : "Not Started"}
          />
          <StatCard
            icon={assets.ic_fire_orange}
            color="lightorange"
            title="Daily Streak"
            value={`${learningRes?.daily_streak} Days`}
          />
          <StatCard
            icon={assets.ic_price_dark_red}
            color="red"
            title="Current Goal Streak"
            value={`${learningRes?.current_goal_streak} Days`}
          />
          <StatCard
            icon={assets.ic_graph_blue}
            color="indigo"
            title="Overall Progress"
            value={`${Math.round(overallProgress)}%`}
          />
        </section>
        <section className="goal_warning_box">
          💡 Complete at least one learning activity every day to keep your
          streak alive.
          <br />
          Missing a day resets your daily streak, but your completed videos,
          quizzes and overall progress remain saved.
        </section>
        <button className="continue_btn" onClick={onClose}>
          Continue Learning →
        </button>
      </section>
    </section>
  );
}

/* ── Dashboard Layout ── */
export const DashboardLayout = ({
  user,
  dashboardData,
  loading,
  videos,
  actionCards = defaultActionCards,
  aiAssets,
  videoLimit = 2,
}) => {
  const { get, post } = useApi();
  const { mail, examType, role } = useSingleton();

  // ── Upcoming Tests State & Logic ──
  const [upcomingTests, setUpcomingTests] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  const handleReloadUpcoming = () => {
    setReloadTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchUpcomingTests = async () => {
      const studentEmail = (
        mail ||
        user?.data?.email ||
        user?.email ||
        localStorage.getItem("mail") ||
        localStorage.getItem("user_email") ||
        ""
      ).trim();

      if (!studentEmail) {
        setUpcomingLoading(false);
        return;
      }

      setUpcomingLoading(true);

      try {
        let res = await get(apiStudentUpcomingTests(studentEmail));
        let dataObj = res?.data || res;
        let list = [];

        if (Array.isArray(dataObj?.upcoming_tests)) {
          list = dataObj.upcoming_tests;
        } else if (Array.isArray(dataObj?.data?.upcoming_tests)) {
          list = dataObj.data.upcoming_tests;
        } else if (Array.isArray(dataObj?.data)) {
          list = dataObj.data;
        } else if (Array.isArray(dataObj)) {
          list = dataObj;
        }

        if (isMounted) {
          setUpcomingTests(list);
        }
      } catch (err) {
        console.error("Error fetching upcoming tests for", studentEmail, err);
        if (isMounted) {
          setUpcomingTests([]);
        }
      } finally {
        if (isMounted) {
          setUpcomingLoading(false);
        }
      }
    };

    fetchUpcomingTests();

    return () => {
      isMounted = false;
    };
  }, [mail, user, reloadTrigger]);

  // ── Video popup state ──
  const [activeVideo, setActiveVideo] = useState(null);
  const [showCarousel, setShowCarousel] = useState(false);
  const [showAskBox, setShowAskBox] = useState(true);
  const [question, setQuestion] = useState("");
  const [videoRes, setVideoRes] = useState({});
  const [audioRes, setAudioRes] = useState({});
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const inputRef = useRef(null);

  const navigate = useNavigate();

  const recommendedVideos = getRecommendedVideos(dashboardData);
  const recentVideos = getRecentVideos(videos, dashboardData);
  const hasRecommended = recommendedVideos.length > 0;
  const allVideos = hasRecommended ? recommendedVideos : recentVideos;
  const displayedVideos = allVideos.slice(0, videoLimit);

  // ── Reset MediaPopup state on video change ──
  useEffect(() => {
    if (activeVideo) {
      setShowAskBox(true);
      setQuestion("");
      setVideoRes({});
      setAudioRes({});
    }
  }, [activeVideo]);

  // ── API: Mark video completed ──
  const videoMarkCompleted = async (videoUrl) => {
    const payload = {
      user_email: mail,
      video_url: videoUrl,
      course_type: examType,
      filters: activeVideo?.filters,
    };
    try {
      await post(API_VIDEO_MARK, payload);
    } catch (e) {
      console.log("ERROR: VIDEO MARK COMPLETED", e);
    }
  };

  console.log(activeVideo, "cdc");

  // ── API: AI Video Chat ──
  const handleAiVideoChat = async () => {
    if (!activeVideo) {
      console.error("activeVideo is null when handleAiVideoChat is called.");
      return;
    }

    const payload = {
      Question: question,
      filters: activeVideo?.filters || {},
      language: "english",
    };
    try {
      const res = await post(API_VIDEO_CHAT, payload);
      if (res) {
        setVideoRes(res?.data);
      }
    } catch (e) {
      console.log("ERROR: AI VIDEO CHAT", e);
    }
  };

  // ── Goal modal (via useCommonDashboard) ──
  const {
    learningRes,
    showGoalModal,
    showSuccessModal,
    showViewChallenge,
    learnHistroyRes,
    learningChallengeCreate,
    setShowGoalModal,
    setShowSuccessModal,
    setShowViewChallenge,
  } = useCommonDashboard();

  if (loading)
    return (
      <div className="loader-container">
        <div className="gradient-loader"></div>
      </div>
    );

  return (
    <section className="dashboard-wrapper">
        <main className="main-content">
          {/* ── Hero Banner ── */}
          <section className="hero-banner">
            <div className="hero-copy">
              <p className="hero-overline">Today at Rovian</p>
              <h1>
                Welcome,{" "}
                <span className="user-name">{user?.data?.name || "Alex"}</span>
              </h1>
              <p>
                Continue where you left off, complete your key tasks, and keep
                your learning streak active.
              </p>
            </div>
            <div className="hero-visual">
              <div className="hero-streak-card">
                <div className="streak-icon">
                  <Flame size={20} />
                </div>
                <div className="streak-info">
                  <span>Current Streak</span>
                 
                  <strong>
                    {dashboardData?.quick_stats?.study_streak ?? 0}{" "}
                    {(dashboardData?.quick_stats?.study_streak ?? 0) <= 1
                      ? "day"
                      : "days"}
                  </strong>
                </div>
              </div>
            </div>
          </section>

          {/* ── Action Cards ── */}
          <section className="suite-cards">
            {actionCards.map((card) => (
              <ActionCard
                key={card.title}
                {...card}
                examType={examType}
                role={role}
              />
            ))}
          </section>

          <section className="cards-row">
            {/* ── What's Next ── */}
            <div className="cr-card">
              <div className="cr-card-header">
                <span className="cr-card-title">What's Next</span>
                {allVideos.length > videoLimit && (
                  <button
                    className="cr-link"
                    onClick={() => setShowCarousel(true)}
                  >
                    View More
                  </button>
                )}
              </div>
              <div className="vd-grid">
                {displayedVideos.map((video) => (
                  <div
                    key={video.id}
                    className="vd-card"
                    onClick={() => setActiveVideo(video)}
                  >
                    <div className="vd-thumb-wrap">
                      <video
                        src={video.video_url}
                        muted
                        preload="metadata"
                        className="vd-thumb"
                      />
                      <div className="vd-play-btn">
                        <span className="vd-play-icon">▶</span>
                      </div>
                      {video.duration && (
                        <span className="vd-dur-badge">{video.duration}</span>
                      )}
                    </div>
                    <div className="vd-info">
                      <p className="vd-title">{video.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Upcoming Tests ── */}
            <div className="cr-card">
              <div className="cr-card-header">
                <span className="cr-card-title">Upcoming Tests</span>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <button
                    className="ut-reload-btn"
                    onClick={handleReloadUpcoming}
                    disabled={upcomingLoading}
                    title="Reload Upcoming Tests"
                  >
                    <RotateCw
                      size={12}
                      className={upcomingLoading ? "spin-icon" : ""}
                    />
                    <span>Reload</span>
                  </button>
                  <span className="ut-badge">
                    {upcomingLoading ? "Loading..." : `${upcomingTests.length} Scheduled`}
                  </span>
                </div>
              </div>
              <div className="ut-list">
                {upcomingLoading ? (
                  <div style={{ padding: "20px", textAlign: "center", color: "#64748b", fontSize: "13px" }}>
                    Loading upcoming tests...
                  </div>
                ) : upcomingTests.length === 0 ? (
                  <div style={{ padding: "20px", textAlign: "center", color: "#64748b", fontSize: "13px" }}>
                    No upcoming tests scheduled.
                  </div>
                ) : (
                  upcomingTests.map((item, index) => {
                    const subjectClean = (item.subject || item.exam || "general").toLowerCase().replace(/[^a-z0-9]/g, "");
                    const formattedTime = item.scheduled_date && item.scheduled_time
                      ? `${item.scheduled_date}, ${item.scheduled_time}`
                      : item.scheduled_date || item.scheduled_time || "Scheduled";

                    const statusClean = (item.status || "").toLowerCase();
                    const isInactive = statusClean === "missed" || statusClean === "expired" || statusClean === "completed";
                    const buttonLabel = statusClean === "missed"
                      ? "Test Missed"
                      : statusClean === "expired"
                      ? "Test Expired"
                      : statusClean === "completed"
                      ? "Completed"
                      : `Join Test (${item.room_code})`;

                    return (
                      <div className="ut-item" key={item.room_code || index}>
                        <div className="ut-item-header">
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span className={`ut-subject-pill ${subjectClean}`}>
                              {item.subject || item.exam || "Test"}
                            </span>
                            {item.status && (
                              <span className={`ut-status-badge ${statusClean}`}>
                                {statusClean === "ongoing" && (
                                  <span className="ut-status-dot">●</span>
                                )}
                                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                              </span>
                            )}
                          </div>
                          <span className="ut-time-badge">{formattedTime}</span>
                        </div>
                        <h4 className="ut-title">{item.test_name}</h4>
                        <div className="ut-meta">
                          <span>⏱ {item.duration_minutes || 60} Mins</span>
                          <span>•</span>
                          <span>🏷️ {item.mode === "live" ? "Live Mode" : "Async Mode"}</span>
                          {item.chapter && (
                            <>
                              <span>•</span>
                              <span>📖 {item.chapter}</span>
                            </>
                          )}
                        </div>
                        <button
                          className={`ut-action-btn ${isInactive ? "disabled" : "primary"}`}
                          disabled={isInactive}
                          onClick={() => {
                            if (isInactive) return;
                            const basePath = window.location.pathname.split("/dashboard")[0];
                            const targetQuizRoute = basePath === "" ? "/quiz" : `${basePath}/quiz`;
                            navigate(targetQuizRoute, {
                              state: { roomCode: item.room_code },
                            });
                          }}
                        >
                          {buttonLabel}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* ── Learning Analytics ── */}
            <div className="cr-card">
              <div className="cr-card-header">
                <span className="cr-card-title">Learning Analytics</span>
                <span className="la-badge">This Week</span>
              </div>
              <div className="la-metrics">
                <section className="la_metrics_inside">
                  <div className="la-metric">
                    <div className="la-metric-icon la-icon-blue">
                      <BookOpen size={16} />
                    </div>
                    <div className="la-metric-body">
                      <span className="la-metric-label">Study Hours</span>
                      <strong className="la-metric-val">
                        {dashboardData?.learning_analytics?.study_hours ?? 0}h
                      </strong>
                    </div>
                    {/* <span className="la-trend la-trend-up">
                      <TrendingUp size={10} />
                    </span> */}
                  </div>
                  <div className="la-metric">
                    <div className="la-metric-icon la-icon-purple">
                      <Target size={16} />
                    </div>
                    <div className="la-metric-body">
                      <span className="la-metric-label">Chapters</span>
                      <strong className="la-metric-val">
                        {dashboardData?.learning_analytics
                          ?.chapters_completed ?? 0}
                      </strong>
                    </div>
                    {/* <span className="la-trend la-trend-up">
                      <TrendingUp size={10} /> 1
                    </span> */}
                  </div>
                  <div className="la-metric">
                    <div className="la-metric-icon la-icon-green">
                      <Brain size={16} />
                    </div>
                    <div className="la-metric-body">
                      <span className="la-metric-label">Test accuracy</span>
                      <strong className="la-metric-val">
                        {dashboardData?.learning_analytics?.test_accuracy ?? 0}%
                      </strong>
                    </div>
                    {/* <span className="la-trend la-trend-down">
                      <TrendingDown size={10} /> 2%
                    </span> */}
                  </div>
                </section>
                <section className="la_metrics_inside">
                  <div className="la-metric">
                    <div className="la-metric-icon la-icon-blue">
                      <img src={assets.ic_circle_checked} alt="checked" />
                    </div>
                    <div className="la-metric-body">
                      <span className="la-metric-label">
                        Total Mcq completed
                      </span>
                      <strong className="la-metric-val">
                        {dashboardData?.video_progress?.total_mcqs_completed ??
                          0}
                      </strong>
                    </div>
                    {/* <span className="la-trend la-trend-up">
                      <TrendingUp size={10} />
                    </span> */}
                  </div>
                  <div className="la-metric">
                    <div className="la-metric-icon la-icon-purple">
                      <img src={assets.ic_circle_timer} alt="timer" />
                    </div>
                    <div className="la-metric-body">
                      <span className="la-metric-label">
                        Total Mcq Remaining
                      </span>
                      <strong className="la-metric-val">
                        {dashboardData?.video_progress?.total_mcqs_remaining ??
                          0}
                      </strong>
                    </div>
                    {/* <span className="la-trend la-trend-up">
                      <TrendingUp size={10} /> 1
                    </span> */}
                  </div>
                  <div className="la-metric">
                    <div className="la-metric-icon la-icon-green">
                      <img src={assets.ic_circle_play} alt="play" />
                    </div>
                    <div className="la-metric-body">
                      <span className="la-metric-label">
                        Total Video Watched
                      </span>
                      <strong className="la-metric-val">
                        {dashboardData?.video_progress?.total_videos_watched ??
                          0}
                      </strong>
                    </div>
                    {/* <span className="la-trend la-trend-down">
                      <TrendingDown size={10} /> 2%
                    </span> */}
                  </div>
                </section>
              </div>
              {role !== "guest" && (
                <section
                  className="view_challange_wrapper"
                  onClick={() => setShowViewChallenge(true)}
                >
                  <h4>View Challenges</h4>
                </section>
              )}
            </div>
          </section>
        </main>

        {/* ── Carousel Modal ── */}
        {showCarousel && (
          <VideoCarouselModal
            videos={allVideos}
            onClose={() => setShowCarousel(false)}
            onPlay={(video) => {
              setActiveVideo(video);
              setShowCarousel(false);
            }}
          />
        )}

        {/* ── MediaPopup — all state & API logic lives here ── */}
        {activeVideo && (
          <MediaPopup
            data={{
              type: "video",
              url: activeVideo.video_url,
              topic: activeVideo.title,
              display_name: activeVideo.subtitle || activeVideo.title,
              content: "",
            }}
            onClose={() => {
              setActiveVideo(null);
              setShowAskBox(false);
            }}
            videoRef={videoRef}
            audioRef={audioRef}
            showAskBox={showAskBox}
            setShowAskBox={setShowAskBox}
            question={question}
            setQuestion={setQuestion}
            inputRef={inputRef}
            audioRes={audioRes}
            videoRes={videoRes}
            aiVideoChat={activeVideo ? handleAiVideoChat : null}
            aiAudioChat={null}
            isMarkReadVisible={true}
            markVideo={(url) => videoMarkCompleted(url)}
            role={role}
          />
        )}

      {/* ── Goal Modal ── */}
      {showGoalModal && (
        <GoalModal
          createChallenge={learningChallengeCreate}
          setShowGoalModal={() => setShowGoalModal(false)}
        />
      )}

      {/* ── Goal Success Modal ── */}
      {showSuccessModal && (
        <GoalSuccessModal
          learningRes={learningRes}
          onClose={() => setShowSuccessModal(false)}
        />
      )}

      {showViewChallenge && (
        <GoalSuccessViewChanllengeModal
          learningRes={learnHistroyRes}
          onClose={() => setShowViewChallenge(false)}
        />
      )}
    </section>
  );
};

/* ── NEET Dashboard entry point — no API logic here ── */
export const Dashboard = () => {
  const { user, dashboardData, loading } = useHandler();

  return (
    <DashboardLayout
      aiAssets={ROUTE_LEARNING_ASSETS}
      user={user}
      dashboardData={dashboardData}
      loading={loading}
      videoLimit={2}
    />
  );
};
